import { ContextGLMipFilter } from '../base/ContextGLMipFilter';
import { ContextGLTextureFilter } from '../base/ContextGLTextureFilter';
import { ContextGLWrapMode } from '../base/ContextGLWrapMode';
import { ContextWebGL } from './ContextWebGL';
import { ContextWebGLFlags } from './ContextWebGLFlags';
import { SamplerStateWebGL } from './SamplerStateWebGL';
var nPOTAlerts = {};
var TextureContextWebGL = /** @class */ (function () {
    function TextureContextWebGL(_context) {
        var _a, _b, _c, _d;
        this._context = _context;
        this._lastestBoundedStack = [];
        this._samplerStates = [];
        this._textureTypeDictionary = {};
        this._wrapDictionary = {};
        this._filterDictionary = {};
        this._mipmapFilterMap = {};
        this._activeTexture = -1;
        this._renderTargetConfig = undefined;
        var gl = _context._gl;
        this._textureTypeDictionary = {
            texture2d: gl.TEXTURE_2D,
            textureCube: gl.TEXTURE_CUBE_MAP
        };
        this._wrapDictionary = (_a = {},
            _a[ContextGLWrapMode.REPEAT] = gl.REPEAT,
            _a[ContextGLWrapMode.CLAMP] = gl.CLAMP_TO_EDGE,
            _a);
        this._filterDictionary = (_b = {},
            _b[ContextGLTextureFilter.LINEAR] = gl.LINEAR,
            _b[ContextGLTextureFilter.NEAREST] = gl.NEAREST,
            _b);
        this._mipmapFilterMap[ContextGLTextureFilter.LINEAR] = (_c = {},
            _c[ContextGLMipFilter.MIPNEAREST] = gl.LINEAR_MIPMAP_NEAREST,
            _c[ContextGLMipFilter.MIPLINEAR] = gl.LINEAR_MIPMAP_LINEAR,
            _c[ContextGLMipFilter.MIPNONE] = gl.LINEAR,
            _c);
        this._mipmapFilterMap[ContextGLTextureFilter.NEAREST] = (_d = {},
            _d[ContextGLMipFilter.MIPNEAREST] = gl.NEAREST_MIPMAP_NEAREST,
            _d[ContextGLMipFilter.MIPLINEAR] = gl.NEAREST_MIPMAP_LINEAR,
            _d[ContextGLMipFilter.MIPNONE] = gl.NEAREST,
            _d);
        //defaults
        for (var i = 0; i < ContextWebGL.MAX_SAMPLERS; ++i) {
            this._samplerStates[i] = new SamplerStateWebGL(i, null, _context.glVersion === 2 ? gl.REPEAT : gl.CLAMP_TO_EDGE, gl.LINEAR, gl.LINEAR);
        }
    }
    TextureContextWebGL.prototype.pushTextureBind = function (target, texture) {
        this._lastestBoundedStack.push(this._lastBoundedTexture);
        this.bindTexture(target, texture);
    };
    TextureContextWebGL.prototype.popTextureBind = function (target) {
        this.bindTexture(target, this._lastestBoundedStack.pop());
    };
    TextureContextWebGL.prototype.bindTexture = function (target, texture) {
        this._lastBoundedTexture = texture;
        this._context._gl.bindTexture(target, texture === null || texture === void 0 ? void 0 : texture._glTexture);
    };
    TextureContextWebGL.prototype.uploadFromArray = function (target, array, miplevel, premultiplied) {
        if (miplevel === void 0) { miplevel = 0; }
        if (premultiplied === void 0) { premultiplied = false; }
        var gl = this._context._gl;
        var width = target._width >>> miplevel;
        var height = target._height >>> miplevel;
        if (array && array.length !== width * height * 4) {
            /* eslint-disable-next-line */
            throw new Error("Array is not the correct length for texture dimensions: expected: " + width * height * 4 + ", exist: " + array.length);
        }
        if (array instanceof Array)
            array = new Uint8Array(array);
        // push lastest used texture to stack and bind new
        this.pushTextureBind(gl.TEXTURE_2D, target);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, premultiplied);
        gl.texImage2D(gl.TEXTURE_2D, miplevel, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, array);
        // restore last texture boundings
        // otherwise we will corrupt state
        this.popTextureBind(gl.TEXTURE_2D);
    };
    TextureContextWebGL.prototype.setTextureAt = function (sampler, texture) {
        var gl = this._context._gl;
        var samplerState = this._samplerStates[sampler];
        if (this._activeTexture !== sampler && (texture || samplerState.type)) {
            this._activeTexture = sampler;
            gl.activeTexture(gl.TEXTURE0 + sampler);
        }
        if (!texture) {
            if (samplerState.type) {
                // disable link to sampler in bounded texture
                if (samplerState.boundedTexture) {
                    samplerState.boundedTexture._state.id = -1;
                }
                this.bindTexture(samplerState.type, null);
                samplerState.boundedTexture = null;
                samplerState.type = null;
            }
            return -1;
        }
        var powerOfTwo = texture.isPOT;
        var isAllowRepeat = (this._context.glVersion === 2 || powerOfTwo)
            && ContextWebGLFlags.PREF_REPEAT_WRAP !== 0 /* NONE */;
        var textureType = this._textureTypeDictionary[texture.textureType];
        samplerState.type = textureType;
        // bind texture only when sampler is not same (texture is not bounded)
        // but there are a bug, and texture can be unbounded
        // if (texture._state.id !== sampler) {
        this.bindTexture(textureType, texture);
        texture._state.id = sampler;
        // }
        // apply state of texture only WHEN IT NOT EQUAL
        if (!texture._state.equals(samplerState)) {
            texture._state.copyFrom(samplerState);
            if (samplerState.wrap === gl.REPEAT && !isAllowRepeat) {
                if (!nPOTAlerts[texture.id]
                    && ContextWebGLFlags.PREF_REPEAT_WRAP === 1 /* ALL_TEXTURES */) {
                    nPOTAlerts[texture.id] = true;
                    console.warn('[Texture] REPEAT wrap not allowed for nPOT textures in WebGL1,' +
                        'set ContextWebGLFlags.PREF_REPEAT_WRAP ' +
                        '= (ContextWebGLPreference.POT_TEXTURES | ContextWebGLPreference.NONE)' +
                        'to supress warnings!', texture);
                }
                gl.texParameteri(textureType, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
                gl.texParameteri(textureType, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
            }
            else {
                gl.texParameteri(textureType, gl.TEXTURE_WRAP_S, samplerState.wrap);
                gl.texParameteri(textureType, gl.TEXTURE_WRAP_T, samplerState.wrap);
            }
            gl.texParameteri(textureType, gl.TEXTURE_MAG_FILTER, samplerState.filter);
            gl.texParameteri(textureType, gl.TEXTURE_MIN_FILTER, samplerState.mipfilter);
        }
        return sampler;
        //gl.uniform1i(this._currentProgram.getUniformLocation(ContextGLProgramType.SAMPLER, sampler), sampler);
    };
    TextureContextWebGL.prototype.setSamplerStateAt = function (sampler, wrap, filter, mipfilter) {
        if (0 <= sampler && sampler < ContextWebGL.MAX_SAMPLERS) {
            this._samplerStates[sampler].wrap = this._wrapDictionary[wrap];
            this._samplerStates[sampler].filter = this._filterDictionary[filter];
            this._samplerStates[sampler].mipfilter = this._mipmapFilterMap[filter][mipfilter];
        }
        else {
            throw 'Sampler is out of bounds.';
        }
    };
    TextureContextWebGL.prototype.unsafeCopyToTexture = function (target, rect, destPoint, scale) {
        if (scale === void 0) { scale = false; }
        var gl = this._context._gl;
        this.pushTextureBind(gl.TEXTURE_2D, target);
        if (!scale) {
            gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, destPoint.x, destPoint.y, rect.x, rect.y, rect.width, rect.height);
        }
        else {
            // this method allow scale up/down for texture with it configuration.
            // need set a sampler state to linear for this texture
            gl.copyTexImage2D(gl.TEXTURE_2D, 0, gl.RGBA, rect.x, rect.y, rect.width, rect.height, 0);
        }
        // restore latest active texture
        this.popTextureBind(gl.TEXTURE_2D);
    };
    TextureContextWebGL.prototype.restoreRenderTarget = function () {
        if (!this._renderTargetConfig) {
            this.setRenderToBackBuffer();
            return;
        }
        var _a = this._renderTargetConfig, 
        /*texture,*/ enableDepthAndStencil = _a.enableDepthAndStencil, antiAlias = _a.antiAlias, surfaceSelector = _a.surfaceSelector, mipmapSelector = _a.mipmapSelector;
        this.setFrameBuffer(this._renderTarget, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector);
    };
    TextureContextWebGL.prototype.setRenderToTexture = function (target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector) {
        if (enableDepthAndStencil === void 0) { enableDepthAndStencil = false; }
        if (antiAlias === void 0) { antiAlias = 0; }
        if (surfaceSelector === void 0) { surfaceSelector = 0; }
        if (mipmapSelector === void 0) { mipmapSelector = 0; }
        if (this._renderTarget) {
            this.presentFrameBuffer(this._renderTarget);
            this._renderTarget._isRT = false;
        }
        this._renderTarget = target;
        this._renderTarget._isRT = true;
        this._renderTargetConfig = {
            texture: this._renderTarget,
            enableDepthAndStencil: enableDepthAndStencil,
            antiAlias: antiAlias,
            surfaceSelector: surfaceSelector,
            mipmapSelector: mipmapSelector
        };
        this._renderTarget._skipPresent = false;
        this.setFrameBuffer(this._renderTarget, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector);
    };
    TextureContextWebGL.prototype.setRenderToBackBuffer = function () {
        if (this._renderTarget) {
            this.presentFrameBuffer(this._renderTarget);
            this._renderTarget._isRT = false;
            this._renderTarget = null;
        }
        var gl = this._context._gl;
        this._renderTargetConfig = null;
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    };
    /*internal*/ TextureContextWebGL.prototype.setFrameBuffer = function (target, enableDepthAndStencil, antiAlias, surfaceSelector, mipmapSelector) {
        var gl = this._context._gl;
        if (gl instanceof WebGLRenderingContext)
            mipmapSelector = 0;
        var width = target.width >>> mipmapSelector;
        var height = target.height >>> mipmapSelector;
        target._mipmapSelector = mipmapSelector;
        if (!target._frameBuffer[mipmapSelector]) {
            this.initFrameBuffer(target, antiAlias, mipmapSelector);
        }
        else {
            gl.bindFramebuffer(gl.FRAMEBUFFER, target._frameBuffer[mipmapSelector]);
        }
        if (enableDepthAndStencil) {
            this._context.enableDepth();
            this._context.enableStencil();
        }
        else {
            this._context.disableDepth();
            this._context.disableStencil();
        }
        this._context.setViewport(0, 0, width, height);
    };
    /*internal*/ TextureContextWebGL.prototype.initFrameBuffer = function (target, antiAlias, mipmapSelector) {
        var gl = this._context._gl;
        var width = target._width >>> mipmapSelector;
        var height = target._height >>> mipmapSelector;
        //create main framebuffer
        var frameBuffer = target._frameBuffer[mipmapSelector] = gl.createFramebuffer();
        //create renderbufferdepth
        var renderBufferDepth = target._renderBufferDepth[mipmapSelector] = gl.createRenderbuffer();
        target._mipmapSelector = mipmapSelector;
        // bind texture
        this.pushTextureBind(gl.TEXTURE_2D, target);
        // apply texture with empty data
        if (!target._isFilled) {
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
            //gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
            target._isFilled = true;
            target._isPMA = true;
        }
        //no Multisample buffers with WebGL1
        if (gl instanceof WebGLRenderingContext || !ContextWebGLFlags.PREF_MULTISAMPLE || antiAlias < 1) {
            // activate framebuffer
            gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
            // activate renderbuffer
            gl.bindRenderbuffer(gl.RENDERBUFFER, renderBufferDepth);
            // set renderbuffer configuration
            gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_STENCIL, target._width, target._height);
            // attach texture to framebuffer
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target._glTexture, 0);
            // attach depth to framebuffer
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, renderBufferDepth);
            target._multisampled = false;
        }
        else {
            antiAlias = Math.min(antiAlias, gl.getParameter(gl.MAX_SAMPLES));
            // create framebuffer for DRAW
            var drawFrameBuffer = target._frameBufferDraw[mipmapSelector] = gl.createFramebuffer();
            // compute levels for texture
            var levels = Math.log(Math.min(target._width, target._height)) / Math.LN2 | 0 + 1;
            // bind DRAW framebuffer
            gl.bindFramebuffer(gl.FRAMEBUFFER, drawFrameBuffer);
            // apply storage for texture
            if (!target._texStorageFlag) {
                gl.texStorage2D(gl.TEXTURE_2D, levels, gl.RGBA8, width, height);
                target._texStorageFlag = true;
            }
            // attach texture to framebuffer to current mipmap level
            gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target._glTexture, target._mipmapSelector);
            gl.bindTexture(gl.TEXTURE_2D, null);
            // bind READ framebuffer
            gl.bindFramebuffer(gl.FRAMEBUFFER, frameBuffer);
            gl.bindRenderbuffer(gl.RENDERBUFFER, renderBufferDepth);
            // attach depth renderbuffer multisampled
            gl.renderbufferStorageMultisample(gl.RENDERBUFFER, antiAlias, gl.DEPTH24_STENCIL8, width, height);
            // set renderbuffer configuration
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_STENCIL_ATTACHMENT, gl.RENDERBUFFER, renderBufferDepth);
            // create READ color renderbuffer for multismapling
            var renderBuffer = target._renderBuffer[mipmapSelector] = gl.createRenderbuffer();
            // bind it
            gl.bindRenderbuffer(gl.RENDERBUFFER, renderBuffer);
            // set config
            gl.renderbufferStorageMultisample(gl.RENDERBUFFER, antiAlias, gl.RGBA8, width, height);
            // attach READ framebuffer
            gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.RENDERBUFFER, renderBuffer);
            target._multisampled = true;
        }
        gl.bindRenderbuffer(gl.RENDERBUFFER, null);
        this.popTextureBind(gl.TEXTURE_2D);
    };
    /*internal*/ TextureContextWebGL.prototype.presentFrameBuffer = function (source) {
        var gl = this._context._gl;
        //no Multisample buffers with WebGL1
        if (gl instanceof WebGLRenderingContext || !source._multisampled || source._skipPresent)
            return;
        var width = source._width >>> source._mipmapSelector;
        var height = source._height >>> source._mipmapSelector;
        if (source._frameBuffer[source._mipmapSelector] === source._frameBufferDraw[source._mipmapSelector]) {
            console.error('Framebuffer loop `presentFrameBuffer`');
            return;
        }
        // bind framebuffer with renderbuffer to READ slot
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, source._frameBuffer[source._mipmapSelector]);
        // bind framebuffer with texture to WRITE slot
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, source._frameBufferDraw[source._mipmapSelector]);
        // ckear
        gl.clearBufferfv(gl.COLOR, 0, [0.0, 0.0, 0.0, 0.0]);
        // copy renderbuffer to texture
        gl.blitFramebuffer(0, 0, width, height, 0, 0, width, height, gl.COLOR_BUFFER_BIT, gl.LINEAR);
    };
    /*internal*/ TextureContextWebGL.prototype.presentFrameBufferTo = function (source, target, rect, point) {
        var gl = this._context._gl;
        var targetFrameBuffer = target.textureFramebuffer;
        if (!targetFrameBuffer) {
            this.initFrameBuffer(target, 0, 0);
            gl.bindFramebuffer(gl.FRAMEBUFFER, null);
            targetFrameBuffer = target.textureFramebuffer;
        }
        if (source._frameBuffer[0] === targetFrameBuffer) {
            console.error('Framebuffer loop `presentFrameBufferTo`');
            return;
        }
        // disable next present to avoid dirty texture
        source._skipPresent = true;
        if (!source || !source._multisampled || gl instanceof WebGLRenderingContext) {
            // direct copy to target texture.
            // same as call this._context.renderToTexture
            gl.bindFramebuffer(gl.FRAMEBUFFER, source._frameBuffer[0]);
            this.unsafeCopyToTexture(target, rect, point);
        }
        else if (targetFrameBuffer) {
            // bind framebuffer with renderbuffer to READ slot
            gl.bindFramebuffer(gl.READ_FRAMEBUFFER, source._frameBuffer[0]);
            // bind framebuffer with texture to WRITE slot
            gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, targetFrameBuffer);
            // clear
            // gl.clearBufferfv(gl.COLOR, 0, [0.0, 0.0, 0.0, 0.0]);
            // copy renderbuffer to texture
            gl.blitFramebuffer(rect.x | 0, rect.y | 0, (rect.width + rect.x) | 0, (rect.height + rect.y) | 0, point.x | 0, point.y | 0, (point.x + rect.width) | 0, (point.y + rect.height) | 0, gl.COLOR_BUFFER_BIT, gl.LINEAR);
        }
        this.blitTextureToRenderbuffer(target);
        // needs, because we rebound buffers to copy
        // otherwith Stage not restore rebounds an we will draw to other framebuffer
        this.restoreRenderTarget();
    };
    /**
     * Blit self texture to renderbuffer if a multisampled
     */
    /*internal*/ TextureContextWebGL.prototype.blitTextureToRenderbuffer = function (target) {
        var gl = this._context._gl;
        if (!target._multisampled || !(gl instanceof WebGL2RenderingContext)) {
            return;
        }
        if (target._frameBuffer[target._mipmapSelector] === target._frameBufferDraw[target._mipmapSelector]) {
            console.error('Framebuffer loop `blitTextureToRenderbuffer`');
            return;
        }
        var width = target._width >>> target._mipmapSelector;
        var height = target._height >>> target._mipmapSelector;
        // bind framebuffer with renderbuffer to DRAW slot
        gl.bindFramebuffer(gl.DRAW_FRAMEBUFFER, target._frameBuffer[target._mipmapSelector]);
        // bind framebuffer with texture to READ slot
        gl.bindFramebuffer(gl.READ_FRAMEBUFFER, target._frameBufferDraw[target._mipmapSelector]);
        // clear
        gl.clearBufferfv(gl.COLOR, 0, [0.0, 0.0, 0.0, 0.0]);
        // copy texture to renderbuffer!
        gl.blitFramebuffer(0, 0, width, height, 0, 0, width, height, gl.COLOR_BUFFER_BIT, gl.LINEAR);
    };
    /*internal*/ TextureContextWebGL.prototype.disposeTexture = function (texture) {
        var gl = this._context._gl;
        if (this._renderTarget === texture) {
            console.warn('[Context] Trying to dispose a active tendertarget!');
            this._context.setRenderToBackBuffer();
        }
        if (texture === this._lastBoundedTexture) {
            this._lastBoundedTexture = null;
        }
        if (this._lastestBoundedStack.length && this._lastestBoundedStack.indexOf(texture)) {
            this._lastestBoundedStack.length = 0;
        }
        // delete texture
        gl.deleteTexture(texture._glTexture);
        for (var key in texture._frameBuffer) {
            gl.deleteFramebuffer(texture._frameBuffer[key]);
            gl.deleteFramebuffer(texture._frameBufferDraw[key]);
            gl.deleteRenderbuffer(texture._renderBuffer[key]);
            gl.deleteRenderbuffer(texture._renderBufferDepth[key]);
        }
    };
    return TextureContextWebGL;
}());
export { TextureContextWebGL };
